import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import KpiCard from "@/components/KpiCard";
import TrustBadge from "@/components/TrustBadge";
import TrustRadar from "@/components/TrustRadar";
import AiChatDrawer from "@/components/ai/AiChatDrawer";
import { Calendar, Star, Users, TrendingUp, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function DoctorDashboard() {
  const [doctor, setDoctor] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [appts, setAppts] = useState([]);

  useEffect(() => {
    (async () => {
      const [d, a, ap] = await Promise.all([
        api.get("/doctor/me"),
        api.get("/doctor/analytics"),
        api.get("/doctor/appointments"),
      ]);
      setDoctor(d.data);
      setAnalytics(a.data);
      setAppts(ap.data);
    })();
  }, []);

  const upcoming = appts.filter((a) => a.status === "scheduled").slice(0, 5);
  const criteria = analytics?.trust_metrics
    ? Object.entries(analytics.trust_metrics).map(([name, score]) => ({ name, score }))
    : [];

  return (
    <AppShell
      pageTitle={`Welcome, ${doctor?.name?.replace("Dr. ", "") ? "Dr. " + doctor.name.replace("Dr. ", "") : "Doctor"}`}
      pageDescription="Your practice, verified and quantified. Trust score, patient flow, and reputation at a glance."
      actions={
        <div className="flex items-center gap-2">
          <AiChatDrawer
            endpoint="/ai/doctor-chat"
            title="Clinical Co-pilot"
            description="Ask about differentials, workup plans, or quick summaries over your own patients. Reasoning only — never prescribes."
            buttonLabel="Clinical Co-pilot"
            starterPrompts={[
              "Summarise my upcoming appointments and flag anything unusual",
              "Draft a prevention-plan note for this patient based on their top opportunities",
              "What's a reasonable workup for new-onset hypertension in a 45-year-old?",
              "What are the latest ICMR guidelines on prediabetes management?",
            ]}
            caseSwitcher={{
              listEndpoint: "/doctor/patients",
              labelKey: "name",
              idKey: "id",
              payloadKey: "patient_id",
              allLabel: "No specific patient (general reasoning)",
            }}
            testid="doctor-ai"
          />
          {doctor && <TrustBadge tier={doctor.tier} score={doctor.trust_score} />}
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Trust Index" value={analytics?.trust_score ?? "—"} icon={Star} tone="sky" testid="doctor-kpi-trust" />
        <KpiCard label="Total appointments" value={analytics?.totals?.total ?? 0} icon={Calendar} tone="mint" testid="doctor-kpi-total" />
        <KpiCard label="Patient satisfaction" value={analytics?.reputation?.patient_satisfaction ?? 82} unit="%" icon={Users} tone="lemon" testid="doctor-kpi-satisfaction" />
        <KpiCard label="Referrals · month" value={analytics?.reputation?.referrals_last_month ?? 0} icon={TrendingUp} tone="peach" testid="doctor-kpi-referrals" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-border bg-white p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-2xl text-primary">Trust Index breakdown</h2>
            <Link to="/doctor/analytics" className="text-sm text-primary hover:underline" data-testid="link-doctor-analytics">Full analytics</Link>
          </div>
          {criteria.length > 0 && <TrustRadar criteria={criteria} height={320} />}
        </div>

        <div className="rounded-lg border border-border bg-white p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-2xl text-primary">Upcoming</h2>
            <Link to="/doctor/appointments" className="text-sm text-primary hover:underline" data-testid="link-doctor-appointments">All</Link>
          </div>
          {upcoming.length === 0 ? (
            <div className="text-sm text-muted-foreground">No upcoming appointments.</div>
          ) : (
            <ul className="divide-y divide-border" data-testid="doctor-upcoming-list">
              {upcoming.map((a) => (
                <li key={a.id} className="py-3">
                  <div className="text-sm font-medium text-primary">{a.patient_name}</div>
                  <div className="text-xs text-muted-foreground">{formatDate(a.date)}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{a.reason}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-border bg-primary text-primary-foreground p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="overline text-primary-foreground/70">Complete your profile</div>
          <h3 className="mt-2 font-serif text-2xl">Verified profiles get 3× more patient trust.</h3>
        </div>
        <Link to="/doctor/profile"><span className="inline-flex items-center gap-1.5 text-sm font-medium">Update profile <ArrowRight size={14} /></span></Link>
      </div>
    </AppShell>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}
